import type React from "react";
import type { LucideIcon } from "lucide-react";
import { BarChart3 } from "lucide-react";

export interface EmptyStateProps {
  icon?: LucideIcon;
  title?: string;
  body: string;
  cta?: { label: string; onClick: () => void };
}

/**
 * Shared shell for "nothing to show, but nothing's wrong either" states —
 * no period selected, no data uploaded yet, a filtered list with zero
 * matches. Icon-in-a-muted-circle + heading + body, replacing the mix of
 * emoji (📊 📭 🚨 ⌚ …) previously scattered across tabs so every empty
 * state in the app reads as one visual language.
 */
export function EmptyState({
  icon: Icon = BarChart3,
  title,
  body,
  cta,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Icon className="h-6 w-6 text-muted-foreground" />
      </div>
      {title && <h3 className="font-head font-semibold text-lg mb-2">{title}</h3>}
      <p className="text-sm text-muted-foreground max-w-xs">{body}</p>
      {cta && (
        <button
          type="button"
          onClick={cta.onClick}
          className="mt-4 text-sm font-medium text-phoenix-orange hover:underline"
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}
